import { useEffect, useState, useCallback, useRef } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, HelpCircle, X } from 'lucide-react';
import { isCorporate } from '@/brand';

// ─────────────────────────────────────────────────────────────────────────────
// App-wide dialog system — a branded replacement for window.alert / window.confirm.
//
// Usage (drop-in):
//   import { showAlert, showConfirm } from '@/components/AppDialog';
//   showAlert('Failed to save. Please try again.');
//   if (!(await showConfirm('Delete this file?'))) return;
//
// Mount <DialogHost/> ONCE near the app root.
// ─────────────────────────────────────────────────────────────────────────────

export type DialogVariant = 'info' | 'success' | 'error' | 'warning' | 'confirm';

export interface DialogOptions {
  title?: string;
  variant?: DialogVariant;
  confirmText?: string;
  cancelText?: string;
  /** Style the confirm action as destructive (red). */
  destructive?: boolean;
}

interface DialogRequest {
  id: number;
  message: string;
  options: DialogOptions;
  isConfirm: boolean;
  resolve: (value: boolean) => void;
}

let enqueueFn: ((req: DialogRequest) => void) | null = null;
const pending: DialogRequest[] = [];
let nextId = 1;

// Heuristic variant detection from the message text, so legacy `alert(...)` calls
// get sensible colouring without having to touch every call site.
function inferVariant(message: string): DialogVariant {
  const m = message.toLowerCase();
  if (/(success|successfully|sent|saved|complete|done|✅|🎉)/.test(m)) return 'success';
  if (/(fail|failed|error|could ?not|couldn't|unable|wrong|denied|expired|invalid)/.test(m)) return 'error';
  if (/(please|required|missing|select|enter|fill|wait|must|first)/.test(m)) return 'warning';
  return 'info';
}

function request(message: unknown, options: DialogOptions, isConfirm: boolean): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const req: DialogRequest = {
      id: nextId++,
      message: String(message ?? ''),
      options: options || {},
      isConfirm,
      resolve,
    };
    if (enqueueFn) enqueueFn(req);
    else pending.push(req); // buffered until <DialogHost/> mounts
  });
}

/** Branded replacement for window.alert. Resolves when dismissed. */
export function showAlert(message: unknown, options: DialogOptions = {}): Promise<void> {
  return request(message, options, false).then(() => undefined);
}

/** Branded replacement for window.confirm. Resolves true on confirm, false on cancel. */
export function showConfirm(message: unknown, options: DialogOptions = {}): Promise<boolean> {
  return request(message, { variant: 'confirm', ...options }, true);
}

const NEUTRAL = isCorporate ? '#2159B8' : '#1976D2';
const NEUTRAL_SOFT = isCorporate ? 'rgba(232,137,11,.13)' : 'rgba(25,118,210,.12)';

const PALETTE: Record<DialogVariant, { accent: string; soft: string; Icon: typeof Info; title: string }> = {
  success: { accent: '#0E8F6E', soft: 'rgba(14,143,110,.12)',  Icon: CheckCircle2,  title: 'Success' },
  error:   { accent: '#C0392B', soft: 'rgba(192,57,43,.12)',   Icon: XCircle,       title: 'Something went wrong' },
  warning: { accent: '#E08A1E', soft: 'rgba(224,138,30,.14)',  Icon: AlertTriangle, title: 'Heads up' },
  // The two neutral variants carry the brand accent (amber on
  // connectcorporate.com, blue on conncct.com) so a confirm button never lands
  // blue in an amber app. The other three stay semantic on both brands.
  info:    { accent: NEUTRAL, soft: NEUTRAL_SOFT, Icon: Info,          title: 'Notice' },
  confirm: { accent: NEUTRAL, soft: NEUTRAL_SOFT, Icon: HelpCircle,    title: 'Please confirm' },
};

export function DialogHost() {
  const [queue, setQueue] = useState<DialogRequest[]>([]);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    enqueueFn = (req) => setQueue((q) => [...q, req]);
    if (pending.length) {
      setQueue((q) => [...q, ...pending]);
      pending.length = 0;
    }
    return () => { enqueueFn = null; };
  }, []);

  const current = queue[0];

  const close = useCallback((result: boolean) => {
    setQueue((q) => {
      const [first, ...rest] = q;
      if (first) first.resolve(result);
      return rest;
    });
  }, []);

  // Focus the primary button + wire keyboard shortcuts whenever a dialog appears.
  useEffect(() => {
    if (!current) return;
    const id = window.setTimeout(() => confirmBtnRef.current?.focus(), 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(false); }
      else if (e.key === 'Enter') { e.preventDefault(); close(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.clearTimeout(id); window.removeEventListener('keydown', onKey); };
  }, [current, close]);

  if (!current) return null;

  const variant: DialogVariant = current.options.variant || (current.isConfirm ? 'confirm' : inferVariant(current.message));
  const { accent, soft, Icon, title } = PALETTE[variant];
  const heading = current.options.title || title;
  const confirmText = current.options.confirmText || (current.isConfirm ? 'Confirm' : 'OK');
  const cancelText = current.options.cancelText || 'Cancel';
  const confirmBg = current.options.destructive ? '#C0392B' : accent;

  return (
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(false); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(3px)',
        animation: 'appdlg-fade .16s ease-out',
      }}
    >
      <style>{`
        @keyframes appdlg-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes appdlg-pop  { from { opacity: 0; transform: translateY(8px) scale(.97) } to { opacity: 1; transform: none } }
        @media (prefers-reduced-motion: reduce) {
          .appdlg-card, [style*="appdlg-fade"] { animation: none !important; }
        }
      `}</style>
      <div
        className="appdlg-card"
        role={current.isConfirm ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-label={heading}
        style={{
          width: '100%', maxWidth: 420, background: '#fff', borderRadius: 18,
          boxShadow: '0 24px 60px -18px rgba(15,23,42,.45)', overflow: 'hidden',
          fontFamily: 'inherit', animation: 'appdlg-pop .2s cubic-bezier(.2,.8,.2,1)',
        }}
      >
        <div style={{ padding: '26px 26px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{
              flexShrink: 0, width: 44, height: 44, borderRadius: 12, background: soft,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon className="w-6 h-6" style={{ color: accent }} />
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
              <h3 style={{ fontSize: 17, fontWeight: 800, color: '#17263A', letterSpacing: '-.01em', margin: 0 }}>{heading}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.5, color: '#42505F', margin: '6px 0 0', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{current.message}</p>
            </div>
            <button
              onClick={() => close(false)}
              aria-label="Close"
              style={{ flexShrink: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#9AA4B2', padding: 2, lineHeight: 0, borderRadius: 8 }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '0 26px 22px' }}>
          {current.isConfirm && (
            <button
              onClick={() => close(false)}
              style={{
                border: '1.5px solid #E2E6EC', background: '#fff', color: '#42505F',
                fontFamily: 'inherit', fontWeight: 700, fontSize: 14, padding: '10px 18px',
                borderRadius: 10, cursor: 'pointer',
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            ref={confirmBtnRef}
            onClick={() => close(true)}
            style={{
              border: 'none', background: confirmBg, color: '#fff',
              fontFamily: 'inherit', fontWeight: 800, fontSize: 14, padding: '10px 20px',
              borderRadius: 10, cursor: 'pointer', boxShadow: `0 8px 20px -8px ${confirmBg}`,
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DialogHost;
